import { GRID_CHECKBOX_SELECTION_COL_DEF, GridColDef } from '@mui/x-data-grid'
import { Link } from '@mui/material'
import TypeLabel from '../../../shared/UI/TypeLabel'
import { getPalleteTypeByProp } from '../../../theme/palette'
import { getLocalDateString } from '../../../utils/date-utils'
import StatusLabel from '../../../shared/UI/StatusLabel'
import { EnhancementStub, SubmitStatus } from '../../../models/enhancement'

const getHoursCellValue = (row: { hours?: number }): number | undefined =>
  row.hours ? row.hours : undefined

const getShiftsCellValue = (row: { shifts?: number | null }): number | undefined =>
  row.shifts ? row.shifts : undefined

const testid = 'enhancement-summary-'

function headerText(testId: string, headerName?: string) {
  return (
    <div style={{ fontWeight: 'bold', color: 'black' }} data-testid={testId}>
      {headerName}
    </div>
  )
}
const calculateStatusLabel = (row: EnhancementStub) => {
  switch (row.submitStatus) {
    case SubmitStatus.AMENDED:
      return 'Pending Submission'
    case SubmitStatus.APPROVED:
      return 'Pending Submission'
    case SubmitStatus.SUBMITTED:
      return 'Submitted'
    case SubmitStatus.MOVED:
      return row.periodMovedTo ? `Moved to period ${row.periodMovedTo}` : 'Moved'
    default:
      return row.submitStatus
  }
}

export const EnhancementColumns: GridColDef[] = [
  {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
    hideable: false,
    headerName: 'Checkbox Selection'
  },
  {
    field: 'displayName',
    headerName: 'Employee',
    headerClassName: 'header',
    flex: 1.5,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => (
      <div data-testid={`${testid}row-${params.row.enhancementId}`}>
        <Link component="button" variant="body2">
          {params.value}
        </Link>
      </div>
    ),
  },
  {
    field: 'enhancementType',
    headerName: 'Type',
    headerClassName: 'header',
    flex: 1,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => (
      <div data-testid={`cell-enhancementType-${params.row.id}`}>
        <TypeLabel label={getPalleteTypeByProp(params.value).label} type={params.value} />
      </div>
    ),
  },
  {
    field: 'hours',
    headerName: 'Hours',
    headerClassName: 'header',
    flex: 0.5,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => <div data-testid={`cell-hours-${params.row.id}`}>{getHoursCellValue(params.row)}</div>,
  },
  {
    field: 'shifts',
    headerName: 'Shifts',
    headerClassName: 'header',
    flex: 0.5,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => <div data-testid={`cell-shifts-${params.row.id}`}>{getShiftsCellValue(params.row)}</div>,
  },
  {
    field: 'comments',
    headerName: 'Comments',
    headerClassName: 'header',
    flex: 1.5,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => <div data-testid={`cell-comments-${params.row.id}`}>{params.value}</div>,
  },
  {
    field: 'approvedBy',
    headerName: 'Approved By',
    headerClassName: 'header',
    flex: 1,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => (
      <div data-testid={`cell-approvedBy-${params.row.id}`}>{params.value}</div>
    ),
  },
  {
    field: 'approvalDate',
    headerName: 'Approval Date',
    headerClassName: 'header',
    flex: 1,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => (
      <div data-testid={`cell-approvalDate-${params.row.id}`}>
        {params.row.approvalDate ? getLocalDateString(new Date(params.row.approvalDate)) : ''}
      </div>
    ),
    sortable: true,
  },
  {
    field: 'dateFrom',
    headerName: 'Date From',
    headerClassName: 'header',
    flex: 1,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => (
      <div data-testid={`cell-dateFrom-${params.row.id}`}>
        {params.row.dateFrom ? getLocalDateString(new Date(params.row.dateFrom)) : ''}
      </div>
    ),
    sortable: true,
  },
  {
    field: 'dateTo',
    headerName: 'Date To',
    headerClassName: 'header',
    flex: 1,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => (
      <div data-testid={`cell-dateTo-${params.row.id}`}>
        {params.row.dateTo ? getLocalDateString(new Date(params.row.dateTo)) : ''}
      </div>
    ),
    sortable: true,
  },
  {
    field: 'submitStatus',
    headerName: 'Status',
    headerClassName: 'header',
    flex: 1.5,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => (
      <div data-testid={`cell-submitStatus-${params.row.id}`}>
        <StatusLabel status={calculateStatusLabel(params.row)} />
      </div>
    ),
  },
]

export const EnhancementMobileColumns: GridColDef[] = [
  {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
    hideable: false,
    headerName: 'Checkbox Selection'
  },
  {
    field: 'displayName',
    headerName: 'Employee',
    headerClassName: 'header',
    flex: 1,
    minWidth: 130,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => (
      <div data-testid={`cell-displayName-${params.row.id}`}>
        <Link component="button" variant="body2">
          {params.value}
        </Link>
      </div>
    ),
  },
  {
    field: 'hours',
    headerName: 'Hours',
    headerClassName: 'header',
    flex: 0.5,
    minWidth: 100,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => <div data-testid={`cell-hours-${params.row.id}`}>{getHoursCellValue(params.row)}</div>,
  },
  {
    field: 'shifts',
    headerName: 'Shifts',
    headerClassName: 'header',
    flex: 0.5,
    minWidth: 100,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => <div data-testid={`cell-shifts-${params.row.id}`}>{getShiftsCellValue(params.row)}</div>,
  },
  {
    field: 'enhancementType',
    headerName: 'Type',
    headerClassName: 'header',
    flex: 1,
    minWidth: 130,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => (
      <div data-testid={`cell-enhancementType-${params.row.id}`}>
        <TypeLabel label={getPalleteTypeByProp(params.value).label} type={params.value} />
      </div>
    ),
  },
  {
    field: 'comments',
    headerName: 'Comments',
    headerClassName: 'header',
    flex: 1,
    minWidth: 130,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => <div data-testid={`cell-comments-${params.row.id}`}>{params.value}</div>,
  },
  {
    field: 'approvedBy',
    headerName: 'Approved By',
    headerClassName: 'header',
    flex: 1,
    minWidth: 130,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => (
      <div data-testid={`cell-approvedBy-${params.row.id}`}>{params.value}</div>
    ),
  },
  {
    field: 'approvalDate',
    headerName: 'Approval Date',
    headerClassName: 'header',
    flex: 1,
    minWidth: 100,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => (
      <div data-testid={`cell-approvalDate-${params.row.id}`}>
        {params.row.approvalDate ? getLocalDateString(new Date(params.row.approvalDate)) : ''}
      </div>
    ),
    sortable: true,
  },
  {
    field: 'dateFrom',
    headerName: 'Date From',
    headerClassName: 'header',
    flex: 1,
    minWidth: 100,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => (
      <div data-testid={`cell-dateFrom-${params.row.id}`}>
        {params.row.dateFrom ? getLocalDateString(new Date(params.row.dateFrom)) : ''}
      </div>
    ),
    sortable: true,
  },
  {
    field: 'dateTo',
    headerName: 'Date To',
    headerClassName: 'header',
    flex: 1,
    minWidth: 100,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => (
      <div data-testid={`cell-dateTo-${params.row.id}`}>
        {params.row.dateTo ? getLocalDateString(new Date(params.row.dateTo)) : ''}
      </div>
    ),
    sortable: true,
  },
  {
    field: 'submitStatus',
    headerName: 'Status',
    headerClassName: 'header',
    flex: 1,
    minWidth: 135,
    renderHeader: params => headerText(params.field, params.colDef.headerName),
    renderCell: params => (
      <div data-testid={`cell-submitStatus-${params.row.id}`}>
        <StatusLabel status={calculateStatusLabel(params.row)} />
      </div>
    ),
  },
]
